import { useEffect, useRef } from 'react';
import { FieldErrors } from 'react-hook-form';

interface useFormAccessibilityHook {
  errorSummaryRef: React.MutableRefObject<HTMLDivElement | null>;
}

const useFormAccessibility = (
  errors: FieldErrors,
  keyMapper: { [key: string]: string | null } = {}
): useFormAccessibilityHook => {
  const errorSummaryRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const errorMessages = getErrorMessages(errors, keyMapper);

    if (errorMessages.length > 0) {
      const summaryText = `There are ${
        errorMessages.length
      } errors on this form. Please fix the following: ${errorMessages.join('. ')}.`;

      if (errorSummaryRef.current) {
        errorSummaryRef.current.textContent = summaryText;
      }
    } else {
      if (errorSummaryRef.current) {
        errorSummaryRef.current.textContent = '';
      }
    }
  }, [errors, keyMapper]);

  return { errorSummaryRef };
};

// A robust recursive helper to extract all error messages from a complex object.
const getErrorMessages = (errors, keyMapper = {}) => {
  const messages: string[] = [];
  const visited = new Set();

  const traverse = (obj, parentPath = '') => {
    // If we've already visited this object, stop to prevent infinite recursion

    if (!obj || typeof obj !== 'object' || visited.has(obj)) {
      return;
    }
    visited.add(obj);

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        traverse(item, `${parentPath}[${index}]`);
      });

      return;
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        const mappedKey = keyMapper[currentPath] !== undefined ? keyMapper[currentPath] : keyMapper[key];

        if (mappedKey === null) {
          continue;
        }

        if (value && typeof value === 'object' && value.message) {
          const finalKeyName = typeof mappedKey === 'string' ? mappedKey : key;

          messages.push(`${finalKeyName}: ${value.message}`);
        } else if (value && typeof value === 'object') {
          traverse(value, currentPath);
        }
      }
    }
  };

  traverse(errors);

  return messages;
};

export default useFormAccessibility;
